import { useAuth } from "@/context/AuthContext";
import { useCurrencyContext } from "@/context/CurrencyContext";
import { useTimezoneContext } from "@/context/TimezoneContext";
import { LogoutOutlined, RightOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd/lib";
import { Avatar, Dropdown, Select, Typography, Space } from "antd/lib";
import type { PropsWithChildren } from "react";
import { useRouter } from "next/navigation";
import { useLanguageContext } from "@/context/LanguageContext";
import { isEmpty } from "lodash";
import { IMAGE_CDN } from "@/constant/apiPath";

export default function UserPopover({ children }: PropsWithChildren) {
  const { user, logout } = useAuth();
  const { listCurrencies, changeCurrency, currentCurrency } = useCurrencyContext();
  const { timezoneSettings, timezone, onChangeTimezone } = useTimezoneContext();
  const { language } = useLanguageContext();
  const router = useRouter();

  const userSettingsItems: MenuProps["items"] = [
    {
      key: "account",
      label: (
        <div
          className="flex items-center bg-black bg-opacity-5 rounded-lg p-3 gap-3"
          onClick={() => router.push(`/${language}/profile`)}
        >
          <Avatar
            src={
              !isEmpty(user?.profile_information?.avatar)
                ? `${IMAGE_CDN}${user?.profile_information?.avatar}`
                : "https://api.dicebear.com/7.x/miniavs/svg?seed=2"
            }
            size="large"
          />
          <div className="min-w-[200px]">
            <div className="font-bold">{user?.username}</div>
            <div className="text-sm font-medium">Manage my account</div>
          </div>
          <RightOutlined className="ml-auto" />
        </div>
      ),
    },
    { type: "divider" },
    {
      key: "status",
      type: "group",
      label: <span className="text-black">My Status</span>,
      children: [
        {
          key: "basic-feature",
          label: (
            <div className="flex justify-between items-center">
              <Typography.Text>Basic Feature</Typography.Text>
              <div className="w-3 h-3 rounded-full bg-[#52c41a]" />
            </div>
          ),
        },
        {
          key: "user-status",
          label: (
            <div className="flex justify-between items-center">
              <Typography.Text>Status</Typography.Text>
              <div className="px-3 py-1 bg-[#EEF2FF] rounded-lg">
                <Typography.Text className="text-[#3861FB] font-medium">PRO</Typography.Text>
              </div>
            </div>
          ),
        },
      ],
    },
    { type: "divider" },
    {
      key: "timezone",
      type: "group",
      label: (
        <div className="flex justify-between items-center">
          <Typography.Text>Timezone</Typography.Text>
          <Select
            value={timezone?.text}
            style={{ width: 140 }}
            onSelect={text => onChangeTimezone?.(text)}
            onClick={e => e.stopPropagation()}
          >
            {timezoneSettings?.map(t => (
              <Select.Option value={t.text} key={t.text}>
                <span className="flex items-center gap-2">{t.text}</span>
              </Select.Option>
            ))}
          </Select>
        </div>
      ),
    },
    {
      key: "currency",
      type: "group",
      label: (
        <div className="flex justify-between items-center">
          <Typography.Text>Currency</Typography.Text>
          <Select
            value={currentCurrency?.code}
            style={{ width: 140 }}
            onChange={value => changeCurrency?.(value)}
            onClick={e => e.stopPropagation()}
          >
            {listCurrencies?.map(c => (
              <Select.Option key={c.code} value={c.code}>
                <Space>
                  {c.code}
                  <Typography.Text type="secondary">({c.symbol})</Typography.Text>
                </Space>
              </Select.Option>
            ))}
          </Select>
        </div>
      ),
    },
    { type: "divider" },
    {
      key: "signout",
      label: "Sign out",
      itemIcon: <LogoutOutlined />,
      onClick: () => logout(),
    },
  ];

  return (
    <Dropdown menu={{ items: userSettingsItems }} trigger={["click"]} placement="bottomRight">
      {children}
    </Dropdown>
  );
}
